import React from "react";
import {observer} from "mobx-react-lite";
import * as d3 from "d3";
import {Box, ButtonBase, CircularProgress, Stack, Typography} from "@mui/material";
import {useStores} from "~/stores";
import type {DonutChartSupplierSpendData} from "~/services/ApiTypes";
import type {SelectionResult} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MultiSelection} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {ChartCard} from "./ChartCard";
import {otherShare} from "./chartHelpers";

const SIZE = 220;
const START_COLOR = '#1B1B1F';
const END_COLOR = '#8E9099';
const OTHER_COLOR = '#E3E2E6';

type Row = DonutChartSupplierSpendData;

/**
 * Donut of the top suppliers (or parent suppliers) and the rest, with a clickable legend.
 * Click selects one; ctrl-click excludes one.
 */
export const SpendBreakdownCard: React.FC<{toggle?: React.ReactNode}> = observer(({toggle}) => {
    const {p, bagStore} = useStores();
    const manager = bagStore.advanced;
    const byParent = manager.groupParentSuppliersFilter;
    const selection: SelectionResult<string> | undefined = byParent ? manager.filters.sp_ids : manager.filters.s_ids;
    const onSelectionChange = byParent ? manager.parentSupplierFilterChanged : manager.supplierFilterChanged;

    // Do not show other if there is no other spend
    const rows: Row[] = [...manager.spendDonutData]
        .filter(d => !(d.s_id === null && !d.spend))
        .sort((a, b) => a.s_id === null ? 1 : b.s_id === null ? -1 : b.spend - a.spend);
    const total = rows.reduce((sum, r) => sum + Math.max(0, r.spend), 0);
    const named = rows.filter(r => r.s_id !== null);
    const colorScale = d3.scaleLinear<string>([0, Math.max(1, named.length - 1)], [START_COLOR, END_COLOR]);
    const colorOf = (r: Row, i: number) => r.s_id === null ? OTHER_COLOR : colorScale(i);
    const share = otherShare(rows);
    const selectedIds = new Set(selection?.selected.map(o => String(o.id)) ?? []);
    const hasSelection = MultiSelection.isActive(selection);
    const isSelected = (r: Row) => r.s_id !== null && (selection?.inclusive ? selectedIds.has(r.s_id) : !selectedIds.has(r.s_id));

    const onClick = (r: Row, ctrl: boolean) => {
        if (r.s_id === null) return; // Other
        if (named.length === 1) {
            onSelectionChange({inclusive: true, selected: []});
        } else if (ctrl) {
            const s = MultiSelection.exclusiveToggle(selection, r.s_id);
            if (s) onSelectionChange(s);
        } else {
            onSelectionChange(MultiSelection.inclusiveToggle(selection, r.s_id));
        }
    };

    const arcs = d3.pie<Row>().sort(null).value(r => Math.max(0, r.spend))(rows);
    const arc = d3.arc<d3.PieArcDatum<Row>>().innerRadius(SIZE / 2 - 22).outerRadius(SIZE / 2 - 2);
    const unitLabel = byParent ? (p.p.parentSupplierAltName?.toLowerCase() ?? 'parent supplier') : 'supplier';

    return <ChartCard
        title="Spend breakdown"
        subtitle={`Distribution by top ${unitLabel}s`}
        actions={toggle}
        fill>
        {/* Takes the height of the supplier spend card next to it; the donut and the list are centred in it */}
        {manager.advancedSpendDonutDataRM.busy
            ? <Stack alignItems="center" justifyContent="center" className="spend-breakdown"><CircularProgress/></Stack>
            : rows.length === 0
                ? <Stack alignItems="center" justifyContent="center" className="spend-breakdown">
                    <Typography color="text.secondary">No spend in these filters</Typography>
                </Stack>
                : <Stack direction="row" gap={3} alignItems="center" className="spend-breakdown">
                    <Box sx={{position: 'relative', width: SIZE, height: SIZE, flexShrink: 0}}>
                        <svg width={SIZE} height={SIZE} viewBox={`${-SIZE / 2} ${-SIZE / 2} ${SIZE} ${SIZE}`} role="img"
                             aria-label="Spend breakdown donut">
                            {arcs.map((a, i) => <path
                                key={a.data.s_id ?? 'other'}
                                d={arc(a) ?? ''}
                                fill={colorOf(a.data, i)}
                                stroke="#fff"
                                strokeWidth={1.5}
                                opacity={hasSelection && !isSelected(a.data) && a.data.s_id !== null ? 0.35 : 1}
                                style={{cursor: a.data.s_id === null ? 'default' : 'pointer'}}
                                onClick={e => onClick(a.data, e.ctrlKey)}>
                                <title>{a.data.group}</title>
                            </path>)}
                        </svg>
                        <Stack className="spend-breakdown-center" alignItems="center" justifyContent="center">
                            {share !== null
                                ? <>
                                    <Typography variant="h6" fontWeight={700} className="tabular">{(share * 100).toFixed(1)}%</Typography>
                                    <Typography variant="caption" color="text.secondary">OTHER</Typography>
                                </>
                                : <>
                                    <Typography variant="h6" fontWeight={700} className="tabular">{named.length}</Typography>
                                    <Typography variant="caption" color="text.secondary">{unitLabel.toUpperCase()}S</Typography>
                                </>}
                        </Stack>
                    </Box>
                    <Stack sx={{flex: 1, minWidth: 0}} gap={0.25} className="spend-breakdown-legend">
                        {rows.map((r, i) => <ButtonBase
                            key={r.s_id ?? 'other'}
                            disabled={r.s_id === null}
                            className={'spend-breakdown-legend-row' + (isSelected(r) && hasSelection ? ' selected' : '')}
                            onClick={e => onClick(r, e.ctrlKey)}>
                            <Box className="spend-landscape-legend-swatch" sx={{background: colorOf(r, i)}}/>
                            <Typography variant="body2" noWrap title={r.group} sx={{flex: 1, minWidth: 0, textAlign: 'left'}}>
                                {r.group}
                            </Typography>
                            <Typography variant="body2" className="tabular" sx={{flexShrink: 0}}>
                                {r.s_id === null && total > 0
                                    ? `${(Math.max(0, r.spend) / total * 100).toFixed(1)}%`
                                    : <CurrencyComponent v={r.spend} nDigits={3}/>}
                            </Typography>
                        </ButtonBase>)}
                    </Stack>
                </Stack>}
    </ChartCard>;
});

